import {Pipe, PipeTransform} from '@angular/core';
import {MediaObjectInterface} from '../interfaces/media-object.interface';
import {MediaTypeEnum} from '../enums/media-type.enum';

export interface MediaObjectOptionsInterface {
  formatType?:
    | 'thumb_sm'
    | 'thumb_md'
    | 'thumbnail'
    | 'timeline'
    | 'original'
    | 'webp'
    | 'm3u8'
    | 'm3u8_timeline'
    | 'm3u8_thumbnail'
    | 'document_thumbnail';
  defaultImageType?: 'background' | 'avatar' | 'car' | 'motorcycle' | 'loading';
}

@Pipe({
  name: 'mediaObject',
})
export class MediaObjectPipe implements PipeTransform {
  private static webpSupport: boolean | null = null;

  constructor() {
  }

  static transformToLink(value: MediaObjectInterface, options: MediaObjectOptionsInterface = {}): string {
    let defaultImage: string;
    switch (options.defaultImageType) {
      case 'background':
        defaultImage = 'assets/img/profile-bg-dark.png';
        break;
      case 'car':
        defaultImage = 'assets/img/car.webp';
        break;
      case 'motorcycle':
        defaultImage = 'assets/img/motorcycle.webp';
        break;
      case 'loading':
        defaultImage = null;
        break;
      case 'avatar':
      default:
        defaultImage = 'assets/img/avatar.png';
    }

    if (value?.type === MediaTypeEnum.VIDEO && value.hyperlinks) {
      const preferredFormat = options.formatType || 'm3u8_timeline';
      const preferredNames = MediaObjectPipe.getPreferredNames(preferredFormat, MediaObjectPipe.browserSupportsWebp(), true);
      const uri = preferredNames
        .map((name) => value.hyperlinks.find((e) => e.name === name)?.uri)
        .find((candidate) => !!candidate);
      return uri || defaultImage;
    }

    if (value && value.hyperlinks && value.hyperlinks.length) {
      const preferredFormat = options.formatType || 'thumb_sm';
      const preferredNames = MediaObjectPipe.getPreferredNames(preferredFormat, MediaObjectPipe.browserSupportsWebp(), false);
      const uri = preferredNames
        .map((name) => value.hyperlinks.find((e) => e.name === name)?.uri)
        .find((candidate) => !!candidate);
      return uri || defaultImage;
    }
    return defaultImage;
  }

  private static getPreferredNames(format: string, supportsWebp: boolean, isVideo: boolean): string[] {
    if (isVideo) {
      switch (format) {
        case 'thumb_sm':
        case 'thumbnail':
        case 'm3u8_thumbnail':
          return supportsWebp
            ? ['video_thumb_sm_webp', 'video_thumb_sm', 'm3u8_thumbnail', 'video_thumb_md_webp', 'video_thumb_md', 'm3u8_timeline']
            : ['video_thumb_sm', 'm3u8_thumbnail', 'video_thumb_md', 'm3u8_timeline'];
        case 'thumb_md':
        case 'timeline':
        case 'm3u8_timeline':
          return supportsWebp
            ? ['video_thumb_md_webp', 'video_thumb_md', 'm3u8_timeline', 'video_thumb_lg_webp', 'video_thumb_lg', 'm3u8_thumbnail', 'video_thumb_sm_webp', 'video_thumb_sm']
            : ['video_thumb_md', 'm3u8_timeline', 'video_thumb_lg', 'm3u8_thumbnail', 'video_thumb_sm'];
        default:
          return [format];
      }
    }

    switch (format) {
      case 'thumb_sm':
      case 'thumbnail':
        return supportsWebp
          ? ['thumb_sm_webp', 'thumb_sm', 'webp', 'original']
          : ['thumb_sm', 'original'];
      case 'thumb_md':
      case 'timeline':
        return supportsWebp
          ? ['thumb_md_webp', 'thumb_md', 'webp', 'thumb_sm_webp', 'thumb_sm', 'original']
          : ['thumb_md', 'thumb_sm', 'original'];
      case 'm3u8_timeline':
      case 'm3u8_thumbnail':
        return [format];
      default:
        return [format];
    }
  }

  private static browserSupportsWebp(): boolean {
    if (this.webpSupport !== null) {
      return this.webpSupport;
    }

    if (typeof document === 'undefined') {
      this.webpSupport = false;
      return this.webpSupport;
    }

    const canvas = document.createElement('canvas');
    this.webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;

    return this.webpSupport;
  }

  transform(value: MediaObjectInterface, options: MediaObjectOptionsInterface = {}): string {
    return MediaObjectPipe.transformToLink(value, options);
  }
}
